import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FieldType, FieldTypeConfig, FormlyFieldProps, FormlyModule } from '@ngx-formly/core';
import { AllowedPatternDirective, DialogService } from '@vectronic/util';
import { startWith } from 'rxjs';
import { CustomErrorStateMatcher } from '../../utils/custom-error-state-matcher';
import { AddFrequencyModalComponent } from './add-frequency-modal/add-frequency-modal.component';

const FREQUENCY_FORMAT = /^\d{3}\.\d{3}$/;

@Component({
  selector: 'vectronic-formly-frequency-input',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    FormlyModule,
    MatButtonModule,
    MatIconModule,
    AllowedPatternDirective,
  ],
  templateUrl: './frequency-input.component.html',
  styles: `
    mat-form-field {
      display: block;
    }

    span[matTextSuffix] {
      margin-left: 0.5rem;
    }

    span[matTextPrefix] {
      margin-right: 0.5rem;
    }
  `,
})
export class FrequencyInputComponent
  extends FieldType<FieldTypeConfig<FormlyFieldProps>>
  implements OnInit
{
  private dialogService = inject(DialogService);
  #destroyRef = inject(DestroyRef);
  matcher = new CustomErrorStateMatcher();
  providedFrequencies = signal<string[]>([]);
  containsDuplicates = computed(
    () => new Set(this.providedFrequencies()).size !== this.providedFrequencies().length,
  );

  ngOnInit() {
    this.formControl.addValidators(this.inRangeValidator);
    this.formControl.addValidators(this.numberFormatValidator);
    this.formControl.addValidators(this.patternValidator);
    this.formControl.addValidators(this.lengthValidator);
    this.formControl.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef), startWith(this.formControl.value))
      .subscribe((value: string | null) =>
        this.providedFrequencies.set(
          value
            ?.split(',')
            .map((frequency) => frequency.trim())
            .filter(Boolean) ?? [],
        ),
      );
  }

  onClickAddFrequency() {
    const filledFrequencies = this.formControl.value
      ? this.formControl.value?.split(',')?.length
      : 0;
    const devicesAmount = this.options?.formState?.devicesAmount;
    if (devicesAmount && devicesAmount - filledFrequencies < 1) return;
    this.dialogService.open(AddFrequencyModalComponent, {
      data: {
        devicesAmount: devicesAmount ? devicesAmount - filledFrequencies : undefined,
      },
      afterConfirm: (result) => {
        if (result?.length) {
          if (!this.formControl.value) {
            this.formControl.setValue(`${result.join(',')}`);
          } else {
            this.formControl.setValue(this.formControl.value + `,${result.join(',')}`);
          }
        }
      },
    });
  }

  private inRangeValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    const frequencyList: string[] = control.value.split(',');
    // Malformed entries are reported by the format validators; parsing them would misreport the range.
    for (const frequency of frequencyList.filter((f) => FREQUENCY_FORMAT.test(f))) {
      const number = parseFloat(frequency);
      if (number < 135 || number > 234) {
        return {
          notInRange: {
            message:
              'At least one of the provided frequencies is out of range. Frequencies must be between 135.000 and 234.000, inclusive.',
          },
        };
      }
    }
    return null;
  };

  private patternValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const error = Validators.pattern(/^\d{3}\.\d{3}(,\d{3}\.\d{3})*$/)(control);
    return !error
      ? null
      : {
          pattern: {
            message: 'Provided value does not follow the supported format (135.000,136.000...).',
          },
        };
  };

  private numberFormatValidator: ValidatorFn = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    const frequencyList = control.value.split(',');
    for (const frequency of frequencyList) {
      if (!frequency.length) continue;
      if (!FREQUENCY_FORMAT.test(frequency)) {
        return {
          incorrectNumberFormat: {
            message: 'Each frequency must have exactly 3 digits after the decimal point',
          },
        };
      }
    }
    return null;
  };

  private lengthValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    const length = control.value?.split(',')?.filter(Boolean)?.length;
    const desiredLength = this.options?.formState?.devicesAmount;
    if (desiredLength && length !== desiredLength) {
      return {
        invalidLength: {
          message: `The number of frequencies should be ${desiredLength}, however you entered ${length}. ${desiredLength > length ? 'Please hit the add frequency button' : ''}`,
        },
      };
    }
    return null;
  };

  get frequencyLength() {
    return this.formControl.value?.split(',')?.filter(Boolean).length;
  }

  onPaste(event: ClipboardEvent) {
    const desiredSeparator = ',';
    let pastedText = event.clipboardData.getData('text').trim();
    const possibleSeparatorsRegex = /([,;/]\s*)|\s+/g; // (comma, semicolon or slash followed by zero or many spaces) or (tab or space or new line) zero or many times
    pastedText = pastedText.replace(possibleSeparatorsRegex, desiredSeparator);
    event.preventDefault();
    this.formControl.setValue(pastedText);
  }
}
